import { useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { UploadCloud, FileText, Check, X, Trash2, Search, ClipboardPaste } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { useApi, useQ, errMsg } from "@/lib/api";
import { useT } from "@/lib/i18n";
import { PageHeader, Panel, Empty } from "@/components/common";

const STATUS = { pending: "bg-amber-500/15 text-amber-300", approved: "bg-emerald-500/15 text-emerald-300", rejected: "bg-rose-500/15 text-rose-300" };

const Dropzone = ({ onFiles, busy }) => {
  const { t } = useT();
  const ref = useRef(null);
  const [over, setOver] = useState(false);
  return (
    <div onClick={() => ref.current?.click()} onDragOver={(e) => { e.preventDefault(); setOver(true); }} onDragLeave={() => setOver(false)}
      onDrop={(e) => { e.preventDefault(); setOver(false); onFiles(e.dataTransfer.files); }}
      className={`cursor-pointer rounded-lg border-2 border-dashed p-10 text-center transition-colors ${over ? "border-indigo-500 bg-indigo-500/10" : "border-white/20 hover:bg-white/[0.03]"}`} data-testid="kb-dropzone">
      <UploadCloud className={`h-8 w-8 mx-auto text-indigo-400 ${busy ? "animate-bounce" : ""}`} />
      <p className="font-display font-bold mt-3">{busy ? t("uploading") : t("drop_files")}</p>
      <p className="text-xs text-slate-500 mt-1">PDF · TXT · MD · DOCX · max 10MB</p>
      <input ref={ref} type="file" multiple accept=".pdf,.txt,.md,.docx" className="hidden" onChange={(e) => onFiles(e.target.files)} data-testid="kb-file-input" />
    </div>
  );
};

const PasteDialog = ({ onSaved }) => {
  const { t } = useT();
  const api = useApi();
  const [open, setOpen] = useState(false);
  const [title, setTitle] = useState("");
  const [content, setContent] = useState("");
  const save = async () => {
    try { await api.post("/documents/text", { title, content }); setOpen(false); setTitle(""); setContent(""); onSaved(); toast.success(t("doc_added")); }
    catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button variant="outline" className="rounded-full" data-testid="kb-paste-btn"><ClipboardPaste className="h-4 w-4 mr-1" />{t("paste_text")}</Button></DialogTrigger>
      <DialogContent className="bg-[#111118] border-white/10">
        <DialogHeader><DialogTitle className="font-display">{t("paste_text")}</DialogTitle></DialogHeader>
        <Input placeholder={t("title")} value={title} onChange={(e) => setTitle(e.target.value)} data-testid="kb-paste-title" />
        <Textarea rows={8} placeholder={t("paste_ph")} value={content} onChange={(e) => setContent(e.target.value)} data-testid="kb-paste-content" />
        <Button onClick={save} className="bg-indigo-600 hover:bg-indigo-500" data-testid="kb-paste-save-btn">{t("save")}</Button>
      </DialogContent>
    </Dialog>
  );
};

const SearchBox = () => {
  const { t } = useT();
  const api = useApi();
  const [q, setQ] = useState("");
  const [results, setResults] = useState(null);
  const run = async (e) => { e.preventDefault(); if (!q.trim()) return; const { data } = await api.get("/knowledge/search", { params: { q } }); setResults(data); };
  return (
    <Panel data-testid="kb-search-panel">
      <p className="eyebrow mb-3">{t("kb_search")}</p>
      <form onSubmit={run} className="flex gap-2">
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("kb_search_ph")} data-testid="kb-search-input" />
        <Button type="submit" variant="outline" data-testid="kb-search-btn"><Search className="h-4 w-4" /></Button>
      </form>
      {results && (
        <div className="mt-4 space-y-3 max-h-80 overflow-y-auto scrollbar-thin" data-testid="kb-search-results">
          {results.length === 0 && <p className="text-sm text-slate-500">{t("no_results")}</p>}
          {results.map((r, i) => (
            <div key={i} className="rounded-md bg-white/[0.03] p-3 text-sm">
              <p className="text-xs text-indigo-300 mb-1">{r.document_name} · {r.score}</p>
              <p className="text-slate-300 line-clamp-3">{r.text}</p>
            </div>
          ))}
        </div>
      )}
    </Panel>
  );
};

export default function Knowledge() {
  const { t } = useT();
  const api = useApi();
  const qc = useQueryClient();
  const { data: docs = [] } = useQ("/documents");
  const [busy, setBusy] = useState(false);
  const refresh = () => qc.invalidateQueries({ queryKey: ["/documents"] });

  const upload = async (files) => {
    setBusy(true);
    for (const f of Array.from(files || [])) {
      const fd = new FormData();
      fd.append("file", f);
      try { await api.post("/documents", fd); toast.success(`${f.name} ${t("uploaded")}`); }
      catch (e) { toast.error(`${f.name}: ${errMsg(e)}`); }
    }
    setBusy(false);
    refresh();
  };
  const act = async (id, action) => {
    try {
      if (action === "delete") await api.delete(`/documents/${id}`);
      else await api.post(`/documents/${id}/${action}`);
      refresh();
    } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div>
      <PageHeader eyebrow={t("nav_knowledge")} title={t("kb_title")} subtitle={t("kb_sub")} action={<PasteDialog onSaved={refresh} />} />
      <div className="grid lg:grid-cols-3 gap-4 mb-6">
        <div className="lg:col-span-2"><Dropzone onFiles={upload} busy={busy} /></div>
        <SearchBox />
      </div>
      {docs.length === 0 ? <Empty icon={FileText} title={t("no_docs")} text={t("no_docs_hint")} testId="kb-empty" /> : (
        <div className="rounded-lg border border-white/[0.08] divide-y divide-white/[0.06] bg-[#111118]" data-testid="kb-doc-list">
          {docs.map((d) => (
            <div key={d.id} className="flex flex-col sm:flex-row sm:items-center gap-3 p-4" data-testid={`doc-row-${d.id}`}>
              <FileText className="h-5 w-5 text-slate-500 shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="font-medium truncate">{d.name}</p>
                <p className="text-xs text-slate-500">{d.type.toUpperCase()} · {(d.size / 1024).toFixed(1)} KB · {d.chunk_count} {t("chunks")}</p>
              </div>
              <Badge className={`${STATUS[d.status]} border-0 w-fit`} data-testid={`doc-status-${d.id}`}>{t(`status_${d.status}`)}</Badge>
              <div className="flex gap-1">
                {d.status !== "approved" && <Button size="sm" variant="ghost" onClick={() => act(d.id, "approve")} className="text-emerald-400" data-testid={`doc-approve-${d.id}`}><Check className="h-4 w-4" /></Button>}
                {d.status !== "rejected" && <Button size="sm" variant="ghost" onClick={() => act(d.id, "reject")} className="text-amber-400" data-testid={`doc-reject-${d.id}`}><X className="h-4 w-4" /></Button>}
                <Button size="sm" variant="ghost" onClick={() => act(d.id, "delete")} className="text-rose-400" data-testid={`doc-delete-${d.id}`}><Trash2 className="h-4 w-4" /></Button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
